import React, { useState, useEffect } from "react";
import { Users, UserX, UserMinus, UserCheck, Briefcase, UserCircle, DoorOpen, User, UserRoundX } from "lucide-react";
import Recruitment from "../../components/admin/tabs/Employees/Recruitment";
import Offboarding from "../../components/admin/tabs/Employees/Offboarding";
import MyEmployee from "../../components/admin/tabs/Employees/MyEmployees";
import AddEmployee from "../../components/admin/tabs/Employees/AddEmployee";
import Resignation from "../../components/admin/tabs/Employees/Resignation";
import MyInfo from "../../components/admin/tabs/Employees/MyInfo";
import { getEmployeeData } from "../../utils/localStorage";
import { getRequest } from "../../service";

const tabPermissionMap = {
  myEmployee: "All Employee Access",
  recruitment: "Recruitment",
  addEmployee: null,
  offboarding: "Offboarding",
  resignation: "Resignation",
  myInfo: null,
};

const buildEmployeeStats = (response) => {
  const data = response?.data ?? response ?? {};
  const breakdown = data.statusBreakdown ?? {};

  const totalEmployees = data.totalEmployees ?? 0;
  const activeEmployees = data.activeEmployees ?? breakdown.active ?? 0;
  const inactiveEmployees = data.inactiveEmployees ?? breakdown.inActive ?? 0;

  return [
    {
      title: "Total Employees",
      type: "summary",
      count: totalEmployees,
      active: activeEmployees,
      inactive: inactiveEmployees,
      icon: <UserCheck className="w-6 h-6 text-green-500" />,
      bgColor: "bg-green-50",
    },
    {
      title: "Terminated",
      count: data.terminatedEmployees ?? breakdown.termination ?? 0,
      icon: <UserMinus className="w-6 h-6 text-orange-500" />,
      bgColor: "bg-orange-50",
    },
    {
      title: "Resigned",
      count: data.resignedEmployees ?? breakdown.resigned ?? 0,
      icon: <Users className="w-6 h-6 text-blue-500" />,
      bgColor: "bg-blue-50",
    },
    {
      title: "Lay Off",
      count: data.layOffEmployees ?? breakdown.layOff ?? 0,
      icon: <UserRoundX className="w-6 h-6 text-red-500" />,
      bgColor: "bg-red-50",
    },
  ];
};

const Employees = () => {
  const userData = getEmployeeData();
  const permissions = userData?.designation?.permissions?.Employee || {};
  const role = userData?.role;
  const allEmployeeAccess = permissions["All Employee Access"] || [];
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Define all possible tabs
  const allTabs = [
    { id: "myEmployee", icon: <UserCircle size={20} />, component: <MyEmployee /> },
    { id: "recruitment", icon: <Briefcase size={20} />, component: <Recruitment /> },
    { id: "addEmployee", icon: <User size={20} />, component: <AddEmployee /> },
    { id: "offboarding", icon: <DoorOpen size={20} />, component: <Offboarding /> },
    { id: "myInfo", icon: <User size={20} />, component: <MyInfo /> },
    { id: "resignation", icon: <UserX size={20} />, component: <Resignation /> }
  ];

  let filteredTabs = [];

  if (role === "companyadmin") {
    // Show all tabs except myInfo and resignation, always show addEmployee
    filteredTabs = allTabs.filter(tab => tab.id !== "myInfo" && tab.id !== "resignation");
  } else {
    filteredTabs = allTabs.filter(tab => {
      if (tab.id === "myInfo" || tab.id === "resignation") return true;
      if (tab.id === "addEmployee") {
        // Show addEmployee if All Employee Access includes "add"
        return allEmployeeAccess.includes("add");
      }
      if (tab.id === "myEmployee") {
        // Show myEmployee if All Employee Access is not empty and not just ["add"]
        return Array.isArray(allEmployeeAccess) && allEmployeeAccess.length > 0 && !(allEmployeeAccess.length === 1 && allEmployeeAccess[0] === "add");
      }
      const permKey = tabPermissionMap[tab.id];
      if (!permKey) return true;
      const permArr = permissions[permKey];
      return Array.isArray(permArr) && (permArr.includes("view") || permArr.length > 0);
    });
  }

  // Find the default tab to activate: prefer "myEmployee", else "myInfo", else first available
  const getDefaultTab = () => {
    if (filteredTabs.some(tab => tab.id === "myEmployee")) return "myEmployee";
    if (filteredTabs.some(tab => tab.id === "myInfo")) return "myInfo";
    return filteredTabs[0]?.id || "";
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());

  // If filteredTabs changes and activeTab is not present, set to default
  useEffect(() => {
    if (!filteredTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(getDefaultTab());
    }
    // eslint-disable-next-line
  }, [filteredTabs.map(tab => tab.id).join(","), activeTab]);

  // Fetch employee stats
  useEffect(() => {
    const fetchStats = async () => {
      if (role !== "companyadmin" || !userData?.userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getRequest(`/api/employees/stats?companyId=${userData.userId}`);

        if (response) {
          setStats(buildEmployeeStats(response));
        } else {
          setStats([]);
        }
      } catch (error) {
        console.error("Error fetching employee stats:", error);
        setStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [role, userData?.userId]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const activeComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;
  
  return (
    <div className="flex bg-white">
      <div className="w-64 border-r border-gray-200 bg-gray-50 fixed h-screen">
        <div className="p-4">
          <div className="flex flex-col gap-2">
            {filteredTabs.map((tab) => (
              <div key={tab.id}>
                <button
                  className={`text-left px-4 py-2 rounded-md !capitalize transition-colors duration-300 w-full flex items-center gap-2  cursor-pointer  ${tab.id === activeTab
                    ? "bg-primary text-white"
                    : "hover:bg-gray-100 hover:text-primary "
                    }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.icon}
                  {tab.id.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto ml-64">
        {role === "companyadmin" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {loading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-sm animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              stats.map((stat, index) => (
                <div
                  key={index}
                  className={`${stat.bgColor} p-4 rounded-lg shadow-sm flex items-start justify-between hover:shadow-md transition-shadow`}
                >
                  <div className="flex-1 flex flex-col justify-between min-h-[72px]">
                    <div className="employeeStats">
                      <h3 className="text-gray-600 text-sm font-medium">{stat.title}</h3>
                      <p className="text-2xl font-bold mt-1">{stat.count}</p>
                    </div>
                    {stat.type === "summary" && (
                      <p className="text-xs text-gray-500 mt-2">
                        <span className="text-green-600">Active: {stat.active}</span>
                        <span className="text-gray-400 mx-1">|</span>
                        <span className="text-red-600">Inactive: {stat.inactive}</span>
                      </p>
                    )}
                  </div>
                  {stat.icon}
                </div>
              ))
            )}
          </div>
        )}
        <div className="bg-white rounded-lg shadow-sm">
          {activeComponent}
        </div>
      </div>
    </div>
  );
};

export default Employees;
